// ═══════════════════════════════════════════════════════════════════
// CompanionHUD.js — YOUR COMPANION, AT THE DUEL
// ═══════════════════════════════════════════════════════════════════
// Extracted July 25, 2026 from `Shatterfield.js` (where it lived inside
// <PlayerSide>, which the 3D branch does not render — so the companion
// vanished the moment the field went 3D). Now it is its own file so
// BOTH boards can mount the SAME component: the ONE FACE COROLLARY,
// applied to a second thing.
//
// THE CONTRACT:
//   • Pure presentation. It resolves the local companion from the auth
//     uid itself (no props to thread through the whole match stack) and
//     derives every reaction from two numbers it is handed.
//   • It never animates itself. All the life is EXTERIOR — 4xS-smart:
//       we ATTACK (their Anima drops) → hearts float up
//       we get HURT (our Anima drops) → a POW! smash bursts + a flinch
//   • Art from the one shared CDN source (getCompanionSprite), J'ynxi
//     as the never-blank fallback.
//
// ⚠ MIGRATION NOTE (flagged, not silent). `Shatterfield.js` still
// carries its own private copy of this component (~lines 2197–2392:
// ensureCompanionHudKeyframes / CompFloatHeart / CompanionHUD /
// SF_RADIANT_AURA). That copy is FROZEN and identical to this one. When
// the 10k-line parent is next cut for any other reason, delete that
// block and `import CompanionHUD from "./CompanionHUD";` — this file is
// canon from today. Deliberately deferred rather than re-cutting the
// parent at session close for a purity refactor. Tracked in the roadmap
// beside the GM_DUEL promotion.
//
// FIXED IN THE EXTRACTION (two real bugs, both latent in the original):
//   1. The keyframe guard was id-only, which is exactly the stale-sheet
//      bug that hid Solrend's shattered mark for a day. It compares
//      textContent and replaces on drift now.
//   2. The radiant ring animated on `comp-radiant-pulse`, a keyframe
//      injected by CompanionSprite.js — a component the duel screen
//      never mounts. On the Shatterfield that ring has therefore never
//      pulsed. Renamed to a keyframe this file owns, so it does.
// ═══════════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from "react";
import { auth } from "./firebase";
import { useEquippedCompanion } from "./useEquippedCompanion";
import { getCompanionSprite, getCompanionFallback } from "./SpriteManifest";
import { SD } from "./theme";
// SHIP 10 — the body at the duel. Same square, same pace / bob / flinch
// wrappers, same hearts and POW!; only the picture inside became the
// kit's 3D being, dancing, cheering when we land a hit and flinching
// when we take one. Ids without a body keep their webp.
import CompanionBody3D, { companionBodyOf } from "./companionBody3D";
// KING'S GRACE — the worn Grace, drawn by the shared kit (3D on a body,
// DOM layers on a flat picture). SF_RADIANT_AURA stays exported for any
// reader, but the HUD takes its colour from the Grace registry now.
import { GraceAura2D } from "./GraceAura";
import { getGrace } from "./kingsGrace";

// The duel's warm chrome values this component actually uses. Declared
// locally on purpose: `Shatterfield.js` runs a warm gold-on-brown GM
// that is NOT theme.js's cold violet GM, and importing the wrong one
// would recolor the POW! against the board it sits on. Both palettes
// agree on these two values, so pinning them here is safe AND immune to
// whichever GM the host file happens to have in scope.
const FONT = "'Cinzel', 'Palatino Linotype', serif";
const GOLD_BRIGHT = "#e8b84b";

// Radiant aura colors for the duel companion (mirrors the Plaza ring).
export const SF_RADIANT_AURA = {
  holo_silver:   "#d8d8e8",
  holo_gold:     "#f0d060",
  holo_void:     "#9933ff",
  holo_shadow:   "#7c3aed",
  holo_jynxi:    "#2ee66b",
  holo_depths:   "#00aaff",
  holo_solrend:  "#ff2266",
  holo_aeternus: "#ffffff",
  holo_mythic:   "#ff66cc",
};

// ── Lifecycle-proof keyframes ─────────────────────────────────────
// Injected into <head>, so they survive every React unmount (the
// J'ynxi-vanish lesson: never gate keyframes on a component instance).
// SELF-HEALING: an id-only guard keeps serving a PREVIOUS build's CSS
// after a hot reload or an in-place deploy, and a renamed or retuned
// keyframe then presents as "the effect just doesn't happen."
const COMP_CSS = `
@keyframes sf-comp-pace {
  0%   { transform: translateX(0)            scaleX(1);  }
  47%  { transform: translateX(var(--pace))  scaleX(1);  }
  50%  { transform: translateX(var(--pace))  scaleX(-1); }
  97%  { transform: translateX(0)            scaleX(-1); }
  100% { transform: translateX(0)            scaleX(1);  }
}
@keyframes sf-comp-bob {
  0%,100% { transform: translateY(0); }
  50%     { transform: translateY(-3px); }
}
@keyframes sf-comp-heart {
  0%   { transform: translate(0,0) scale(0.6); opacity: 0; }
  15%  { opacity: 1; }
  100% { transform: translate(var(--hx), -42px) scale(1.1); opacity: 0; }
}
@keyframes sf-comp-pow {
  0%   { transform: scale(0.2) rotate(-12deg); opacity: 0; }
  30%  { transform: scale(1.25) rotate(6deg);  opacity: 1; }
  55%  { transform: scale(0.95) rotate(-3deg); opacity: 1; }
  100% { transform: scale(1.4) rotate(0deg);   opacity: 0; }
}
@keyframes sf-comp-flinch {
  0%,100% { transform: translateX(0); }
  20%     { transform: translateX(-4px) rotate(-4deg); }
  40%     { transform: translateX(4px)  rotate(4deg); }
  60%     { transform: translateX(-3px) rotate(-2deg); }
  80%     { transform: translateX(2px)  rotate(1deg); }
}
@keyframes sf-comp-radiant {
  0%,100% { opacity: 0.7; transform: scale(0.98); }
  50%     { opacity: 1;   transform: scale(1.04); }
}
`;

export function ensureCompanionHudKeyframes() {
  if (typeof document === "undefined") return;
  const id = "sf-companion-hud-kf";
  let el = document.getElementById(id);
  if (el && el.textContent === COMP_CSS) return;
  if (el) el.remove();
  el = document.createElement("style");
  el.id = id;
  el.textContent = COMP_CSS;
  document.head.appendChild(el);
}

// ── useFlashOnChange — the duel hook, verbatim ────────────────────
function useFlashOnChange(value, { duration = 600, direction = false } = {}) {
  const [flash, setFlash] = useState(null); // null | "up" | "down" | "change"
  const prevRef = useRef(value);
  useEffect(() => {
    if (prevRef.current === value) return undefined;
    if (direction) {
      const prev = Number(prevRef.current);
      const next = Number(value);
      if (!Number.isNaN(prev) && !Number.isNaN(next)) setFlash(next > prev ? "up" : "down");
      else setFlash("change");
    } else {
      setFlash("change");
    }
    prevRef.current = value;
    const t = setTimeout(() => setFlash(null), duration);
    return () => clearTimeout(t);
  }, [value, duration, direction]);
  return flash;
}

// ── One floating heart (we landed a hit) ──────────────────────────
function CompFloatHeart({ hx }) {
  return (
    <div style={{
      position: "absolute", left: "50%", bottom: "62%",
      "--hx": `${hx}px`,
      animation: "sf-comp-heart 1.1s ease-out forwards",
      pointerEvents: "none", zIndex: 3,
      fontSize: 16, filter: "drop-shadow(0 0 4px #ff335588)",
    }}>💕</div>
  );
}

// ═══════════════════════════════════════════════════════════════════
export default function CompanionHUD({ myAnima, oppAnima, size = 64 }) {
  ensureCompanionHudKeyframes();

  // Resolve the local companion from auth uid via the failsafe hook —
  // equipped (if still valid) → starter → none. No props to thread.
  const uid = (auth.currentUser && auth.currentUser.uid) || null;
  const { companionId, radiantTier } = useEquippedCompanion(uid);

  // Art + graceful fallback (own art 404 → J'ynxi prototype).
  const ownSrc = companionId ? getCompanionSprite(companionId) : null;
  const [fellBack, setFellBack] = useState(false);
  const prevSrc = useRef(ownSrc);
  useEffect(() => {
    if (prevSrc.current !== ownSrc) { prevSrc.current = ownSrc; setFellBack(false); }
  }, [ownSrc]);
  const src = fellBack ? getCompanionFallback() : ownSrc;

  // Reaction signals derived purely from Anima movement — keeps the HUD
  // self-contained (no rewiring of attack handlers needed).
  const myDrop  = useFlashOnChange(myAnima,  { direction: true, duration: 700 });
  const oppDrop = useFlashOnChange(oppAnima, { direction: true, duration: 700 });

  const [hearts, setHearts] = useState([]); // [{id, hx}]
  const [pow, setPow]       = useState(false);
  const [flinch, setFlinch] = useState(false);
  const heartSeq = useRef(0);
  const [shot, setShot]     = useState(null);   // the body's one-shot: cheer | flinch

  // We hit them → opponent Anima fell → hearts.
  useEffect(() => {
    if (oppDrop !== "down") return undefined;
    const batch = [0, 1, 2].map((i) => ({
      id: ++heartSeq.current,
      hx: -16 + i * 16 + (Math.random() * 8 - 4),
    }));
    setHearts((h) => [...h, ...batch]);
    setShot({ kind: "cheer", at: performance.now() });
    const t = setTimeout(() => {
      setHearts((h) => h.filter((x) => !batch.find((b) => b.id === x.id)));
    }, 1200);
    return () => clearTimeout(t);
  }, [oppDrop]);

  // We got hurt → our Anima fell → POW + flinch.
  useEffect(() => {
    if (myDrop !== "down") return undefined;
    setPow(true);
    setFlinch(true);
    setShot({ kind: "flinch", at: performance.now() });
    const t1 = setTimeout(() => setPow(false), 650);
    const t2 = setTimeout(() => setFlinch(false), 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [myDrop]);

  if (!companionId || !src) return null;

  const aura = radiantTier ? (getGrace(radiantTier)?.color || null) : null;
  const asBody = !!companionBodyOf(companionId);

  return (
    <div style={{
      position: "relative", width: size, height: size,
      pointerEvents: "none", flexShrink: 0,
    }}>
      {/* floating hearts on a hit */}
      {hearts.map((h) => <CompFloatHeart key={h.id} hx={h.hx} />)}

      {/* POW smash on getting hurt */}
      {pow && (
        <div style={{
          position: "absolute", left: "54%", top: "-18%",
          animation: "sf-comp-pow 0.65s ease-out forwards",
          pointerEvents: "none", zIndex: 4,
          fontFamily: FONT, fontWeight: 900, fontStyle: "italic",
          fontSize: size * 0.34, color: SD.red,
          WebkitTextStroke: `1px ${GOLD_BRIGHT}`,
          textShadow: `0 0 10px ${SD.red}cc, 0 2px 4px #000`,
          letterSpacing: 1,
        }}>POW!</div>
      )}


      {/* the pacing companion (outer = pace+flip, inner = bob/flinch) */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 1,
        "--pace": `${Math.round(size * 0.28)}px`,
        animation: "sf-comp-pace 5.5s ease-in-out infinite",
        willChange: "transform",
      }}>
        <div style={{
          width: "100%", height: "100%", position: "relative",
          animation: flinch ? "sf-comp-flinch 0.6s ease-in-out" : "sf-comp-bob 2.4s ease-in-out infinite",
        }}>
          {/* King's Grace rides INSIDE the pace/bob wrapper so the light
              walks with the companion. Flat pictures only; a body wears
              its Grace in its own canvas. */}
          {aura && !asBody && <GraceAura2D graceId={radiantTier} layer="back" />}
          {asBody ? (
            <CompanionBody3D companionId={companionId} size={size} mood="dance" shot={shot} facing={{ x: 0, y: 1 }} grace={aura ? radiantTier : null}
              style={{ filter: aura ? `drop-shadow(0 0 7px ${aura}aa)` : "drop-shadow(0 2px 4px rgba(0,0,0,0.6)) drop-shadow(0 0 5px #c9a84c33)" }} />
          ) : (
          <img
            key={src}
            src={src}
            alt="companion"
            onError={() => { if (!fellBack) setFellBack(true); }}
            style={{
              width: "100%", height: "100%", objectFit: "contain",
              imageRendering: "pixelated", position: "relative", zIndex: 1,
              filter: aura
                ? `drop-shadow(0 0 7px ${aura}aa)`
                : "drop-shadow(0 2px 4px rgba(0,0,0,0.6)) drop-shadow(0 0 5px #c9a84c33)",
            }}
          />
          )}
          {aura && !asBody && <GraceAura2D graceId={radiantTier} layer="front" style={{ zIndex: 2 }} />}
        </div>
      </div>
    </div>
  );
}
